import React from 'react';
import { Response } from '../ui/shadcn-io/ai/response';
import { SmoothStreamingText } from './SmoothStreamingText';
import { cn } from '../../lib/utils';

interface AIResponseProps {
  content: string;
  isStreaming?: boolean;
  className?: string;
  fontSize?: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * AI Response component that renders markdown content using shadcn's Response component.
 * Handles streaming content with smooth character-by-character animation and provides
 * proper markdown rendering with syntax highlighting, math support, and security hardening.
 */
export const AIResponse: React.FC<AIResponseProps> = ({
  content,
  isStreaming = false,
  className,
  fontSize = 'sm'
}) => {
  const fontSizeClasses = {
    xs: 'text-xs [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_h4]:text-xs [&_h5]:text-xs [&_h6]:text-xs [&_p]:text-xs [&_li]:text-xs [&_td]:text-xs [&_th]:text-xs',
    sm: 'text-sm [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base [&_h4]:text-sm [&_h5]:text-sm [&_h6]:text-xs [&_p]:text-sm [&_li]:text-sm [&_td]:text-xs [&_th]:text-xs',
    base: 'text-base [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_h4]:text-base [&_h5]:text-base [&_h6]:text-sm [&_p]:text-base [&_li]:text-base [&_td]:text-sm [&_th]:text-sm',
    lg: 'text-lg [&_h1]:text-3xl [&_h2]:text-2xl [&_h3]:text-xl [&_h4]:text-lg [&_h5]:text-base [&_h6]:text-base [&_p]:text-lg [&_li]:text-lg [&_td]:text-base [&_th]:text-base'
  };

  return (
    <SmoothStreamingText
      content={content}
      isStreaming={isStreaming}
      speed={8} // 8ms per character = 125 chars/second (faster, more responsive)
      animateWhileStreaming={true} // Animate character-by-character while streaming
    >
      {(displayedContent) => (
        <div
          className={cn(
            "transition-all duration-200 ease-out",
            isStreaming && "animate-in fade-in-0 slide-in-from-bottom-1"
          )}
        >
          <Response
            className={cn(fontSizeClasses[fontSize], className)}
            parseIncompleteMarkdown={isStreaming}
          >
            {displayedContent}
          </Response>
        </div>
      )}
    </SmoothStreamingText>
  );
};

export default AIResponse;